import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ThreatVisualization from '../components/home/ThreatVisualization';
import BeforeAfterComparison from '../components/home/BeforeAfterComparison';
import UrgencyBanner from '../components/home/UrgencyBanner';
import SecurityNewsFeed from '../components/home/SecurityNewsFeed';
import ValueProposition from '../components/home/ValueProposition';
import SIEMPillars from '../components/home/SIEMPillars';
import UseCaseSlider from '../components/home/UseCaseSlider';
import ArchitectureDiagram from '../components/home/ArchitectureDiagram';
import TDIRPlatform from '../components/home/TDIRPlatform';
import NistCompliance from '../components/home/NistCompliance';
import Testimonials from '../components/home/Testimonials';
import './Home.css';
import '../components/Navbar.css';

const IconSearch = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <circle cx="27" cy="27" r="16" fill="none" stroke="currentColor" strokeWidth="4" />
    <line x1="39" y1="39" x2="54" y2="54" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const IconTaint = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <path d="M10 44c8-6 14-6 22 0s14 6 22 0" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    <path d="M10 20c8 6 14 6 22 0s14-6 22 0" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    <circle cx="32" cy="32" r="6" fill="currentColor" />
  </svg>
);

const IconIac = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <path d="M16 16 32 8l16 8v16l-16 8-16-8Z" fill="none" stroke="currentColor" strokeWidth="4" />
    <path d="M16 32v16l16 8 16-8V32" fill="none" stroke="currentColor" strokeWidth="4" />
  </svg>
);

const IconSupply = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <rect x="12" y="18" width="40" height="28" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="4" />
    <path d="M12 28h40M24 46V28" fill="none" stroke="currentColor" strokeWidth="4" />
  </svg>
);

const IconNetwork = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <path d="M16 15c8-8 24-8 32 0M22 21c5-5 15-5 20 0M28 27c2-2 6-2 8 0" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    <line x1="32" y1="27" x2="32" y2="43" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    <circle cx="32" cy="47" r="2.5" fill="currentColor" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
    <path d="M32 4l-18 8v16c0 14 18 28 18 28s18-14 18-28v-16l-18-8z" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M32 14v24" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    <path d="M22 26h20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const iconMap = {
  search: <IconSearch />,
  taint: <IconTaint />,
  iac: <IconIac />,
  supply: <IconSupply />,
  network: <IconNetwork />,
  shield: <IconShield />
};

const features = [
  {
    title: 'PassiveGuard',
    subtitle: 'Next-gen SIEM and SOAR',
    description: 'Uncover threats others miss with AI-assisted detection, faster response workflows, and clean integration points for your SOC.',
    icon: 'shield',
    metric: 'Unified SOC'
  },
  {
    title: 'Static Analysis',
    subtitle: 'SAST, Semgrep, Bandit',
    description: 'Inspect source code for hardcoded secrets, SQL injection, XSS, and insecure function calls using proven security engines.',
    icon: 'search',
    metric: 'Code Risk'
  },
  {
    title: 'External Attack Surface',
    subtitle: 'MSP external scanner',
    description: 'Discover exposed assets, misconfigurations, and internet-facing services across customer tenants.',
    icon: 'network',
    metric: 'Asset Context'
  },
  {
    title: 'Taint Tracking',
    subtitle: 'Data flow analysis',
    description: 'Trace untrusted input from request parameters to security-critical sinks like database queries and shell commands.',
    icon: 'taint',
    metric: 'Flow Mapping'
  },
  {
    title: 'Infrastructure as Code',
    subtitle: 'Docker, Kubernetes, Terraform',
    description: 'Scan cloud infrastructure configurations for privileged containers, exposed secrets, and unsafe defaults before deployment.',
    icon: 'iac',
    metric: 'Cloud Guardrails'
  },
  {
    title: 'Supply Chain Security',
    subtitle: 'SCA, OSV, SBOM',
    description: 'Track open-source vulnerabilities and generate CycloneDX software bills of materials for audit-ready reporting.',
    icon: 'supply',
    metric: 'Dependency Risk'
  },
  {
    title: 'Network Analysis',
    subtitle: 'PCAP and protocol inspection',
    description: 'Analyze packet captures to identify insecure protocols, plaintext credentials, and suspicious traffic patterns.',
    icon: 'network',
    metric: 'Traffic Insight'
  }
];

const platformMetrics = [
  { label: 'Signals analyzed', target: 2.4, decimals: 1, prefix: '', suffix: 'M+' },
  { label: 'Mean triage time', target: 5, decimals: 0, prefix: '< ', suffix: 'm' },
  { label: 'Tenant coverage', target: 100, decimals: 0, prefix: '', suffix: '%' }
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const AnimatedStat = ({ target, decimals = 0, prefix = '', suffix = '' }) => {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }

    let frameId;
    let startTime;
    const duration = 1400;

    const step = (timestamp) => {
      if (startTime === undefined) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [target]);

  return (
    <strong>{prefix}{value.toFixed(decimals)}{suffix}</strong>
  );
};

const externalScannerItems = [
  { code: 'ASM', title: 'Asset Discovery', desc: 'Across domains, IPs, and cloud footprints.' },
  { code: 'EXP', title: 'Exposure Detection', desc: 'Shadow services and risky configurations.' },
  { code: 'TLS', title: 'Certificate Monitoring', desc: 'Track expiring or drifting assets.' },
  { code: 'SaaS', title: 'SaaS Domain Coverage', desc: 'Map third-party app exposure.' },
  { code: 'MSP', title: 'Tenant Reporting', desc: 'MSP readiness and compliance tracking.' },
  { code: 'Risk', title: 'Risk Prioritization', desc: 'AI-driven severity scoring and actionable remediation.' }
];

const commandSignals = [
  { label: 'Code', title: 'Static Analysis', detail: 'SAST + dependency findings correlated', icon: 'search', progress: 82 },
  { label: 'Cloud', title: 'Attack Surface', detail: 'External exposure mapped by tenant', icon: 'iac', progress: 68 },
  { label: 'Network', title: 'Traffic Insight', detail: 'PCAP signals enriched with context', icon: 'network', progress: 74 }
];

const alertRows = [
  { severity: 'High', title: 'Credential exposure blocked', source: 'Code + supply chain', time: '12s ago', tone: 'danger' },
  { severity: 'Medium', title: 'Plaintext protocol detected', source: 'Network analyzer', time: '41s ago', tone: 'warning' },
  { severity: 'Low', title: 'Cloud guardrail drift resolved', source: 'IaC scanner', time: '2m ago', tone: 'success' }
];

const commandMetrics = [
  { label: 'Correlated signals', target: 7.8, decimals: 1, suffix: 'k' },
  { label: 'Alert confidence', target: 94, decimals: 0, suffix: '%' },
  { label: 'Time to triage', target: 3, decimals: 0, prefix: '< ', suffix: 'm' }
];

const idsProcessSteps = [
  {
    step: '01',
    title: 'Deploy the Edge Agent',
    detail: 'Build the Suricata-enabled agent image, then launch it from the Agents page with IDS enabled.'
  },
  {
    step: '02',
    title: 'Choose capture mode',
    detail: 'Linux and WSL use live interface capture; Windows watches a mounted PCAP folder for new captures.'
  },
  {
    step: '03',
    title: 'Start IDS monitoring',
    detail: 'Use the Intrusion Detection controls to start or stop Suricata without sending packets to the Hub.'
  },
  {
    step: '04',
    title: 'Triage normalized alerts',
    detail: 'Alerts Center shows IDS detections with severity, flow metadata, signature context, and acknowledgment.'
  }
];

const HeroProductVisual = () => {
  const listVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } }
  };

  return (
    <div className="product-visual">
      <div className="product-topbar">
        <div>
          <span className="product-kicker">PassiveGuard</span>
          <h2>Command Center</h2>
        </div>
        <span className="product-live-badge">
          <span className="hero-status-dot" />
          Live
        </span>
      </div>

      <div className="product-capability-strip" aria-label="PassiveGuard coverage">
        {features.slice(1, 4).map((feature) => (
          <span key={feature.title}>{feature.metric}</span>
        ))}
      </div>

      <div className="product-grid">
        <motion.div
          className="product-signal-panel"
          variants={listVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="product-panel-header">
            <span>Detection stream</span>
            <span className="gh-badge gh-badge-success">Triaged</span>
          </div>

          <div className="product-alert-list">
            {alertRows.map((alert) => (
              <motion.div
                className={`product-alert product-alert-${alert.tone}`}
                key={alert.title}
                variants={rowVariants}
              >
                <span className="product-severity">{alert.severity}</span>
                <div>
                  <strong>{alert.title}</strong>
                  <small>{alert.source}</small>
                </div>
                <time>{alert.time}</time>
              </motion.div>
            ))}
          </div>

          <div className="product-triage">
            <div className="product-triage-copy">
              <span>Automated response path</span>
              <strong>Investigate -> prioritize -> contain</strong>
            </div>
            <div className="product-progress" aria-hidden="true">
              <motion.span
                initial={{ scaleX: prefersReducedMotion() ? 1 : 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.1, ease: 'easeOut', delay: 0.4 }}
              />
            </div>
          </div>

          <svg className="product-sparkline" viewBox="0 0 320 80" role="img" aria-label="Security signal trend">
            <path className="sparkline-fill" d="M0 68 L0 52 C42 40 56 62 92 42 C132 18 158 34 192 24 C232 12 248 38 282 22 C302 12 314 18 320 16 L320 80 L0 80 Z" />
            <motion.path
              className="sparkline-line"
              d="M0 52 C42 40 56 62 92 42 C132 18 158 34 192 24 C232 12 248 38 282 22 C302 12 314 18 320 16"
              pathLength="1"
              initial={{ pathLength: prefersReducedMotion() ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.4, ease: 'easeInOut', delay: 0.2 }}
            />
          </svg>
        </motion.div>

        <div className="product-side-rail">
          {commandSignals.map((signal) => (
            <motion.div
              className="product-signal-card"
              key={signal.label}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.2 }}
            >
              <div className="product-signal-icon">{iconMap[signal.icon]}</div>
              <div>
                <span>{signal.label}</span>
                <strong>{signal.title}</strong>
                <small>{signal.detail}</small>
              </div>
              <div className="product-mini-progress" aria-hidden="true">
                <span style={{ width: `${signal.progress}%` }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="product-metrics">
        {commandMetrics.map((metric) => (
          <div className="product-metric" key={metric.label}>
            <AnimatedStat
              target={metric.target}
              decimals={metric.decimals}
              prefix={metric.prefix}
              suffix={metric.suffix}
            />
            <span>{metric.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const FinalCta = () => (
  <motion.section
    className="feature-section final-cta-section"
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.55, ease: 'easeOut' }}
  >
    <div className="final-cta-card">
      <span className="hero-eyebrow">Ready to operationalize security?</span>
      <h2>Start detecting, triaging, and responding from one workspace.</h2>
      <p>
        Bring code, cloud, network, and response signals together in one operational view
        built for fast setup, sharper context, and clear decisions.
      </p>
      <div className="final-cta-actions">
        <Link to="/register" className="gh-btn gh-btn-primary">Get started</Link>
        <Link to="/platform" className="gh-btn">Explore platform</Link>
      </div>
      <div className="final-cta-trust">
        <span>No new dependencies</span>
        <span>Built for code, cloud, and network signals</span>
      </div>
    </div>
  </motion.section>
);

const Home = () => {
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);
  const isAuthenticated = !!localStorage.getItem('access_token');
  const heroRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isAuthenticated) return undefined;

    const handleScroll = () => {
      setIsHeaderCompact(el.scrollTop > 80);
    };

    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated]);

  const handleHeroMouseMove = (e) => {
    const el = heroRef.current;
    if (!el || prefersReducedMotion()) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty('--spot-x', `${x}%`);
    el.style.setProperty('--spot-y', `${y}%`);
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } }
  };

  const gridContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } }
  };

  const gridItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }
  };

  return (
    <div className="home-container">
      {!isAuthenticated && (
        <header className={`home-header ${isHeaderCompact ? 'compact' : ''}`}>
          <Link to="/home" className="home-logo" aria-label="PassiveGuard home">
            <span className="home-logo-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 2.5 19 5.6v5.2c0 5.4-3.6 9.2-7 10.7-3.4-1.5-7-5.3-7-10.7V5.6L12 2.5Z" />
                <path d="m8.7 12 2.1 2.1 4.6-4.6" />
              </svg>
            </span>
            <span>PassiveGuard</span>
          </Link>

          <nav className="home-nav-links" aria-label="Landing page navigation">
            <div className="dropdown">
              <Link to="/features/workflows" className="navbar-link">Workflows</Link>
              <div className="dropdown-content">
                <Link to="/docs/platform-overview" className="dropdown-item">Platform Overview & Core Components</Link>
                <Link to="/docs/technical-implementation" className="dropdown-item">Technical Implementation & Data Flow</Link>
                <Link to="/docs/advanced-features" className="dropdown-item">Advanced Features & Security Capabilities</Link>
                <Link to="/docs/future-proofing" className="dropdown-item">Future-Proofing & Innovation</Link>
              </div>
            </div>
            <div className="dropdown">
              <Link to="/features/response" className="navbar-link">Response</Link>
              <div className="dropdown-content">
                <Link to="/docs/incident-response" className="dropdown-item">Incident Response & Automation Framework</Link>
                <Link to="/docs/security-operations" className="dropdown-item">Security Operations & Best Practices</Link>
                <Link to="/docs/continuous-improvement" className="dropdown-item">Continuous Improvement</Link>
                <Link to="/docs/nist-csf" className="dropdown-item">NIST CSF Alignment</Link>
              </div>
            </div>
            <div className="dropdown">
              <Link to="/features/integrations" className="navbar-link">Integrations</Link>
              <div className="dropdown-content">
                <Link to="/docs/implementation-deployment" className="dropdown-item">Implementation & Deployment</Link>
                <Link to="/docs/integration-capabilities" className="dropdown-item">Integration Capabilities</Link>
                <Link to="/docs/monitoring-maintenance" className="dropdown-item">Monitoring & Maintenance</Link>
              </div>
            </div>
          </nav>

          <div className="header-actions">
            <Link to="/login" className="gh-btn">Login</Link>
            <Link to="/pricing" className="gh-btn gh-btn-primary">Pricing</Link>
          </div>
        </header>
      )}

      <div className="scroll-container" ref={scrollRef}>
        <section className="hero-section" ref={heroRef} onMouseMove={handleHeroMouseMove}>
          <div className="hero-background" />
          <main className="hero-shell">
            <motion.div
              className="hero-copy"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="hero-status-pill">
                <span className="hero-status-dot" />
                Always-on security intelligence
              </div>
              <span className="hero-eyebrow">Security operations platform</span>
              <h1>
                Turn scattered signals into <span className="hero-gradient-text">decisive action.</span>
              </h1>
              <p>
                PassiveGuard brings SIEM, SOAR, code security, and external attack-surface intelligence
                into one workspace, so your team can investigate with context and respond without delay.
              </p>
              <div className="hero-actions">
                <Link to="/register" className="gh-btn gh-btn-primary">Start securing your environment <span aria-hidden="true">→</span></Link>
                <Link to="/platform" className="gh-btn">See the platform</Link>
              </div>
              <div className="hero-metrics">
                {platformMetrics.map((item) => (
                  <div className="hero-metric" key={item.label}>
                    <AnimatedStat
                      target={item.target}
                      decimals={item.decimals}
                      prefix={item.prefix}
                      suffix={item.suffix}
                    />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="hero-proof" aria-label="PassiveGuard capabilities">
                <span><b>01</b> Code security</span>
                <span><b>02</b> Cloud exposure</span>
                <span><b>03</b> Response automation</span>
              </div>
            </motion.div>

            <motion.div
              className="hero-feature-panel gh-panel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <HeroProductVisual />
            </motion.div>
          </main>
        </section>

        <TDIRPlatform />
        <SIEMPillars />

        <motion.section
          className="feature-section ids-landing-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <div className="container ids-landing-layout">
            <div className="ids-landing-copy">
              <span className="section-subtitle">Live IDS via Edge Agent</span>
              <h2 className="section-title">Run Suricata locally. Send only alert metadata.</h2>
              <p className="section-description">
                PassiveGuard now supports live intrusion detection from the Edge Agent. Packet inspection stays on the user-controlled agent, while the Hub receives deduplicated IDS alerts for investigation and acknowledgment.
              </p>
              <div className="ids-platform-notes">
                <span>Linux/WSL: live interface capture with Docker network and packet-capture capabilities.</span>
                <span>Windows: continuous PCAP folder monitoring for captures exported from local tools.</span>
              </div>
            </div>

            <motion.div
              className="ids-process-grid"
              variants={gridContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {idsProcessSteps.map((item) => (
                <motion.article className="ids-process-card gh-panel" key={item.title} variants={gridItemVariants}>
                  <span>{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.detail}</p>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </motion.section>

        <motion.section
          className="feature-section alt-bg"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <NistCompliance />
        </motion.section>

        <motion.section
          className="feature-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <div className="container scanner-section">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="section-heading"
            >
              <h2 className="section-title center">MSP External Scanner</h2>
              <p className="section-description center">
                Extend visibility across every managed tenant with a unified external attack surface view.
              </p>
            </motion.div>

            <motion.div
              className="scanner-grid"
              variants={gridContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {externalScannerItems.map((item) => (
                <motion.div
                  key={item.title}
                  className="scanner-card gh-panel"
                  variants={gridItemVariants}
                  whileHover={{ y: -4 }}
                >
                  <span className="scanner-code">{item.code}</span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        <UseCaseSlider />
        <ArchitectureDiagram />

        <motion.section
          className="feature-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <div className="container">
            <h2 className="section-title center">Global Threat Intelligence</h2>
            <p className="section-description center">
              Stay ahead of emerging threats with a real-time intelligence feed covering vulnerability disclosures, exploit trends, and advisories relevant to your infrastructure.
            </p>
            <SecurityNewsFeed />
          </div>
        </motion.section>

        <motion.section
          className="feature-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <ThreatVisualization />
        </motion.section>

        <Testimonials />

        <motion.section
          className="feature-section alt-bg"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <BeforeAfterComparison />
        </motion.section>

        <motion.section
          className="feature-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <ValueProposition />
        </motion.section>

        <FinalCta />

        <footer className="home-footer">
          <div className="footer-links">
            <div>
              <strong>Product</strong>
              <ul>
                <li><Link to="/features/workflows">Automation</Link></li>
                <li><Link to="/features/response">Response</Link></li>
                <li><Link to="/features/integrations">Integrations</Link></li>
              </ul>
            </div>
            <div>
              <strong>Company</strong>
              <ul>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li>About Us</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-status">
              <span className="status-indicator"></span>
              System status: secure
            </div>
            <div className="footer-version">v1.0.5 stable</div>
          </div>
        </footer>
      </div>

      <UrgencyBanner />
    </div>
  );
};

export default Home;
